import { useEffect, useState } from 'react';
import { useCurrentUser } from '@/features/auth/api';
import { can } from '@/lib/permissions';

import { CalendarPlus, ChevronLeft, ChevronRight } from 'lucide-react';

import type { Meeting } from '@/api/schema';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { cx } from '@/lib/cx';

import { useMeetings } from '../api';
import { ScheduleDrawer } from '../components/ScheduleDrawer';
import {
  addDays,
  hourLabels,
  isSameDay,
  layOutDay,
  nowOffset,
  startOfDay,
  startOfWeek,
  weekDays,
} from '../layout';
import { MEETING_TYPE_LABEL, MEETING_TYPE_TONE } from '../labels';
import styles from './CalendarPage.module.css';

type View = 'week' | 'day';

/**
 * The calendar.
 *
 * Everything on this screen is in the viewer's own timezone, which needs no conversion
 * step: the server sends instants, a `Date` *is* an instant, and `getHours()` answers
 * locally. Somebody in Dubai and somebody in Pune each see the same meeting at the
 * hour they need to be there.
 */
export function CalendarPage() {
  const { data: user } = useCurrentUser();
  const canCreate = can(user, 'meetings', 'create');
  const [view, setView] = useState<View>('week');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [editing, setEditing] = useState<Meeting | null>(null);
  const [scheduling, setScheduling] = useState<Date | null>(null);

  const days = view === 'week' ? weekDays(anchor) : [startOfDay(anchor)];
  const rangeStart = days[0] ?? startOfDay(anchor);
  const rangeEnd = addDays(days[days.length - 1] ?? rangeStart, 1);

  const meetings = useMeetings(rangeStart, rangeEnd);

  // Redrawn every minute so the line does not sit where it was when the page loaded.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const step = (direction: number) => {
    setAnchor((current) => addDays(current, direction * (view === 'week' ? 7 : 1)));
  };

  const heading = new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
  }).format(rangeStart);

  const line = nowOffset(now);

  return (
    <Page>
      <PageHeader
        title="Calendar"
        description="Every meeting, in your own timezone."
        actions={
          canCreate ? (
            <Button
              icon={<CalendarPlus size={16} />}
              onClick={() => {
                setScheduling(new Date());
              }}
            >
              Schedule a meeting
            </Button>
          ) : null
        }
      />

      <Panel>
        <div className={styles.toolbar}>
          <div className={styles.nav}>
            <Button
              variant="ghost"
              size="sm"
              aria-label={view === 'week' ? 'Previous week' : 'Previous day'}
              onClick={() => {
                step(-1);
              }}
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAnchor(startOfDay(new Date()));
              }}
            >
              Today
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={view === 'week' ? 'Next week' : 'Next day'}
              onClick={() => {
                step(1);
              }}
            >
              <ChevronRight size={16} />
            </Button>
          </div>

          <h2 className={styles.heading}>{heading}</h2>

          <div className={styles.views}>
            <Button
              variant={view === 'week' ? 'subtle' : 'ghost'}
              size="sm"
              aria-pressed={view === 'week'}
              onClick={() => {
                setView('week');
                // Anchored to the week's Monday so switching back and forth does not
                // drift the range under the reader.
                setAnchor((current) => startOfWeek(current));
              }}
            >
              Week
            </Button>
            <Button
              variant={view === 'day' ? 'subtle' : 'ghost'}
              size="sm"
              aria-pressed={view === 'day'}
              onClick={() => {
                setView('day');
              }}
            >
              Day
            </Button>
          </div>
        </div>
      </Panel>

      {meetings.isError ? (
        <ErrorState
          message="The calendar did not load."
          onRetry={() => {
            void meetings.refetch();
          }}
        />
      ) : (
        <Panel flush>
          <div className={styles.calendar}>
            <div className={styles.gutter} aria-hidden="true">
              {hourLabels().map((hour) => (
                <span key={hour} className={styles.hour}>
                  {new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).format(
                    new Date(2026, 0, 1, hour),
                  )}
                </span>
              ))}
            </div>

            <div className={cx(styles.days, view === 'day' && styles.oneDay)}>
              {days.map((day) => {
                const today = isSameDay(day, now);
                return (
                  <section
                    key={day.toISOString()}
                    className={styles.day}
                    aria-label={new Intl.DateTimeFormat(undefined, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    }).format(day)}
                  >
                    <header className={cx(styles.dayHead, today && styles.dayHeadToday)}>
                      <span className={styles.dayName}>
                        {new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(day)}
                      </span>
                      <span className={styles.dayNumber}>{day.getDate()}</span>
                    </header>

                    <div className={styles.column}>
                      {hourLabels().map((hour) => (
                        <button
                          key={hour}
                          type="button"
                          className={styles.slot}
                          disabled={!canCreate}
                          tabIndex={canCreate ? 0 : -1}
                          aria-label={`Schedule at ${hour}:00 on ${new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long' }).format(day)}`}
                          onClick={() => {
                            const at = new Date(day);
                            at.setHours(hour, 0, 0, 0);
                            setScheduling(at);
                          }}
                        />
                      ))}

                      {/* Only on today's column, because a line across the whole week
                          would claim the time is 2pm on Thursday as well. */}
                      {today && line !== null ? (
                        <div
                          className={styles.nowLine}
                          style={{ insetBlockStart: `${line}%` }}
                          aria-hidden="true"
                        />
                      ) : null}

                      {layOutDay(meetings.data ?? [], day).map((block) => (
                        <button
                          key={block.meeting.id}
                          type="button"
                          className={cx(
                            styles.block,
                            styles[MEETING_TYPE_TONE[block.meeting.meeting_type]],
                          )}
                          style={{
                            insetBlockStart: `${block.top}%`,
                            blockSize: `${block.height}%`,
                            // Lanes, so clashing meetings sit beside each other rather
                            // than one hiding the other.
                            insetInlineStart: `${(block.column / block.columns) * 100}%`,
                            inlineSize: `${(1 / block.columns) * 100}%`,
                          }}
                          onClick={() => {
                            setEditing(block.meeting);
                          }}
                        >
                          <span className={styles.blockTime}>
                            {new Intl.DateTimeFormat(undefined, {
                              hour: 'numeric',
                              minute: '2-digit',
                            }).format(new Date(block.meeting.scheduled_at))}
                          </span>
                          <span className={styles.blockTitle}>{block.meeting.title}</span>
                          <span className={styles.blockWho}>
                            {block.meeting.candidate.full_name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>

            {meetings.isPending ? (
              <div className={styles.loading}>
                <Spinner size={18} label="Loading the calendar" />
              </div>
            ) : null}
          </div>
        </Panel>
      )}

      <p className={styles.key}>
        {(Object.keys(MEETING_TYPE_LABEL) as (keyof typeof MEETING_TYPE_LABEL)[]).map(
          (type) => (
            <span key={type} className={styles.keyItem}>
              <span className={cx(styles.keyDot, styles[MEETING_TYPE_TONE[type]])} />
              {MEETING_TYPE_LABEL[type]}
            </span>
          ),
        )}
      </p>

      {editing || scheduling ? (
        <ScheduleDrawer
          meeting={editing}
          initialDate={scheduling ?? undefined}
          onClose={() => {
            setEditing(null);
            setScheduling(null);
          }}
        />
      ) : null}
    </Page>
  );
}
